import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle ,X, Send, Globe } from 'lucide-react';
import { API_URL } from '../data/products';

function Chatbot({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  // NEW: Track conversation history for Groq AI context
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load initial greeting when chatbot opens or language changes
  useEffect(() => {
    if (isOpen) {
      loadInitialGreeting();
    }
  }, [isOpen, selectedLanguage]);

  const loadInitialGreeting = async () => {
    try {
      // Clear messages first to avoid showing stale greetings
      setMessages([]);
      setConversationHistory([]);
      
      const userId = getUserId();
      const response = await fetch(`${API_URL}/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: 'hi', 
          language: selectedLanguage,
          userId: userId,
          conversationHistory: []
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const botMessage = {
          id: Date.now(),
          text: data.message,
          sender: 'bot',
          quickReplies: data.quickReplies || []
        };
        setMessages([botMessage]);
      } else {
        // Fallback greeting if API response is not ok
        setMessages([{
          id: Date.now(),
          text: "Hello! 👋 Welcome to GUDY! I'm here to help you with our premium jaggery products. How can I assist you today?",
          sender: 'bot',
          quickReplies: [
            "Tell me about your products",
            "Wholesale & Bulk Order",
            "Shipping information",
            "Track my order"
          ]
        }]);
      }
    } catch (error) {
      console.error('Failed to load initial greeting:', error);
      // Fallback greeting on error
      setMessages([{
        id: Date.now(),
        text: "Hello! 👋 Welcome to GUDY! I'm here to help you with our premium jaggery products. How can I assist you today?",
        sender: 'bot',
        quickReplies: [
          "Tell me about your products",
          "Wholesale & Bulk Order",
          "Shipping information",
          "Track my order"
        ]
      }]);
    }
  };

 const languages = [
  // Indian Languages
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', category: 'indian' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिंदी', flag: '🇮🇳', category: 'indian' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳', category: 'indian' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳', category: 'indian' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳', category: 'indian' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', flag: '🇮🇳', category: 'indian' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳', category: 'indian' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: '🇮🇳', category: 'indian' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳', category: 'indian' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳', category: 'indian' },
  // International Languages
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', category: 'international' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', category: 'international' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', category: 'international' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', category: 'international', rtl: true }
];

  // Get user ID from localStorage for personalized context
  const getUserId = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const changeLanguage = async (langCode) => {
    setSelectedLanguage(langCode);
    setShowLanguageMenu(false);
    
    // Get initial greeting in new language
    try {
      const userId = getUserId();
      const response = await fetch(`${API_URL}/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: 'hi', 
          language: langCode,
          userId: userId,
          conversationHistory: [] // Reset history on language change
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const botMessage = {
          id: Date.now(),
          text: data.message,
          sender: 'bot',
          quickReplies: data.quickReplies
        };
        setMessages([botMessage]);
        setConversationHistory([]);
      }
    } catch (error) {
      console.error('Language change error:', error);
    }
  };

  const sendMessage = async (text) => {
    const messageText = text || inputMessage;
    if (!messageText || typeof messageText !== 'string' || !messageText.trim()) return;
    
    const userMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'user'
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Update conversation history
    const newHistory = [
      ...conversationHistory,
      { text: messageText, sender: 'user' }
    ];

    try {
      const userId = getUserId();
      
      const response = await fetch(`${API_URL}/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: messageText,
          language: selectedLanguage,
          userId: userId, // Pass user ID for personalized context
          conversationHistory: newHistory.slice(-10) // Send last 10 messages for context
        })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      const botMessage = {
        id: Date.now() + 1,
        text: data.message,
        sender: 'bot',
        quickReplies: data.quickReplies
      };
      setMessages(prev => [...prev, botMessage]);
      
      // Update conversation history with bot response
      setConversationHistory([
        ...newHistory,
        { text: data.message, sender: 'bot' }
      ]);
    } catch (error) {
      console.error('Chatbot error:', error);
      
      const errorMessages = {
        en: "Sorry, I'm having trouble connecting. Please try again or contact support@gudy.com",
        hi: "क्षमा करें, मुझे कनेक्ट करने में समस्या हो रही है। कृपया पुनः प्रयास करें या support@gudy.com से संपर्क करें",
        ta: "மன்னிக்கவும், இணைப்பதில் சிக்கல் உள்ளது. மீண்டும் முயற்சிக்கவும் அல்லது support@gudy.com ஐ தொடர்பு கொள்ளவும்",
        te: "క్షమించండి, నేను కనెక్ట్ చేయడంలో ఇబ్బంది ఎదుర్కొంటున్నాను. దయచేసి మళ్లీ ప్రయత్నించండి లేదా support@gudy.com ను సంప్రదించండి"
      };
      
      const errorMessage = {
        id: Date.now() + 1,
        text: errorMessages[selectedLanguage] || errorMessages.en,
        sender: 'bot',
        quickReplies: selectedLanguage === 'en' 
          ? ["Contact Support", "Try Again"]
          : selectedLanguage === 'hi'
          ? ["सहायता से संपर्क करें", "पुनः प्रयास करें"]
          : selectedLanguage === 'ta'
          ? ["ஆதரவைத் தொடர்பு கொள்ளுங்கள்", "மீண்டும் முயற்சிக்கவும்"]
          : ["మద్దతును సంప్రదించండి", "మళ్లీ ప్రయత్నించండి"]
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickReply = (reply) => {
    const contactKeywords = ["Contact Support", "सहायता से संपर्क करें", "ஆதரவைத் தொடர்பு கொள்ளுங்கள்", "మద్దతును సంప్రదించండి"];
    const tryAgainKeywords = ["Try Again", "पुनः प्रयास करें", "மீண்டும் முயற்சிக்கவும்", "మళ్లీ ప్రయత్నించండి"];
    
    if (contactKeywords.includes(reply)) {
      const contactMessages = {
        en: "📞 Contact Us:\n\nPhone: +91 7373755589\nEmail: support@gudy.com\nWhatsApp: +91 7373755589\n\n🕐 Available: Monday to Saturday, 9 AM - 6 PM",
        hi: "📞 हमसे संपर्क करें:\n\nफोन: +91 7373755589\nईमेल: support@gudy.com\nWhatsApp: +91 7373755589\n\n🕐 उपलब्ध: सोमवार से शनिवार, सुबह 9 बजे - शाम 6 बजे",
        ta: "📞 எங்களை தொடர்பு கொள்ளுங்கள்:\n\nதொலைபேசி: +91 7373755589\nமின்னஞ்சல்: support@gudy.com\nWhatsApp: +91 7373755589\n\n🕐 கிடைக்கும்: திங்கள் முதல் சனி, காலை 9 மணி - மாலை 6 மணி",
        te: "📞 మమ్మల్ని సంప్రదించండి:\n\nఫోన్: +91 7373755589\nఇమెయిల్: support@gudy.com\nWhatsApp: +91 7373755589\n\n🕐 అందుబాటులో: సోమవారం నుండి శనివారం, ఉదయం 9 గంటలు - సాయంత్రం 6 గంటలు"
      };
      
      const contactMessage = {
        id: Date.now(),
        text: contactMessages[selectedLanguage] || contactMessages.en,
        sender: 'bot'
      };
      setMessages(prev => [...prev, contactMessage]);
    } else if (tryAgainKeywords.includes(reply)) {
      setInputMessage('');

    // ── Wholesale quick reply ──
    } else if (reply === 'Wholesale & Bulk Order') {
      const wholesaleMsg = {
        id: Date.now(),
        text: `📦 GUDY Wholesale – Key Details

🏭 Minimum Order: 1 Metric Ton (1,000 kg)
💰 Price: ₹52 per kg (ex-factory, Salem TN)

📊 Pricing Slabs:
• 1 – 4 Tons  →  ₹52 / kg
• 5 – 9 Tons  →  ₹50 / kg
• 10 Tons+    →  Custom quote

✅ 100% pure jaggery, FSSAI certified
📦 Pack sizes: 1kg · 5kg · 25kg · 50kg
🚚 Pan India delivery

🏆 We have successfully dispatched single orders up to 30 Tons!

Ready to enquire?`,
        sender: 'bot',
        quickReplies: ['Place Wholesale Enquiry', 'Wholesale Pricing', 'Contact Support']
      };
      setMessages(prev => [...prev, wholesaleMsg]);

    } else if (reply === 'Wholesale Pricing') {
      const pricingMsg = {
        id: Date.now(),
        text: `💰 GUDY Wholesale Pricing

┌─────────────────────────────┐
│ 1 – 4 Tons   →  ₹52 / kg  │
│ 5 – 9 Tons   →  ₹50 / kg  │
│ 10 Tons+     →  Best rates │
└─────────────────────────────┘

Min. Order Value: ₹52,000 (1 Ton)
GST @5% extra · Freight extra

All prices ex-factory Salem, TN.
Contact us for cube pricing or custom packs.`,
        sender: 'bot',
        quickReplies: ['Place Wholesale Enquiry', 'Contact Support']
      };
      setMessages(prev => [...prev, pricingMsg]);

    } else if (reply === 'Place Wholesale Enquiry') {
      const enquiryMsg = {
        id: Date.now(),
        text: `✅ Here's how to place your wholesale enquiry:

📋 Fill our form:
gudy.com/wholesale

💬 WhatsApp us directly:
+91 7373755589

📧 Email:
office.gudy@gmail.com

Our wholesale team responds within 24 business hours with pricing and availability.

🏆 We've handled single dispatches up to 30 Tons — we're ready for your scale!`,
        sender: 'bot',
        quickReplies: ['Contact Support']
      };
      setMessages(prev => [...prev, enquiryMsg]);

    } else {
      sendMessage(reply);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chatbot-container" style={{
      position: 'fixed',
      bottom: isMobile ? '0' : '20px',
      right: isMobile ? '0' : '20px',
      width: isMobile ? '100vw' : '380px',
      height: isMobile ? '100dvh' : '600px',
      maxWidth: '100vw',
      backgroundColor: 'white',
      borderRadius: isMobile ? '0' : '16px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 2000,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div className="chatbot-header" style={{
        background: 'linear-gradient(135deg, #6B4423 0%, #8B5A3C 100%)',
        color: 'white',
        padding: '14px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        minHeight: '60px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <MessageCircle size={24} color="#6B4423" />
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '16px' }}>GUDY Assistant</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>
              {languages.find(l => l.code === selectedLanguage)?.name || 'English'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Language Selector */}
          <div style={{ position: 'relative' }}> 
            <button 
              onClick={() => setShowLanguageMenu(!showLanguageMenu)} 
              style={{ 
                background: 'white', 
                border: '1px solid #e0e0e0', 
                padding: '6px 8px', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px', 
                fontSize: '13px',
                maxWidth: isMobile ? '110px' : 'unset',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }} 
            > 
              <Globe size={16} /> 
              {languages.find(l => l.code === selectedLanguage)?.flag} {languages.find(l => l.code === selectedLanguage)?.nativeName} 
            </button> 
          
            {showLanguageMenu && ( 
              <div className="language-dropdown" style={{ 
                position: 'absolute', 
                top: '44px', 
                left: 0, 
                background: 'white', 
                border: '1px solid #e0e0e0', 
                borderRadius: '12px', 
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', 
                width: '260px', 
                maxHeight: '50vh', 
                overflowY: 'auto', 
                zIndex: 3000, 
                padding: '8px',
                WebkitOverflowScrolling: 'touch',
              }}> 
                {/* Indian Languages Section */} 
                <div style={{ 
                  padding: '8px 12px', 
                  fontSize: '11px', 
                  fontWeight: '600', 
                  color: '#666', 
                  textTransform: 'uppercase', 
                  borderBottom: '1px solid #f0f0f0' 
                }}> 
                  🇮🇳 Indian Languages 
                </div> 
                {languages.filter(lang => lang.category === 'indian').map(lang => ( 
                  <button 
                    key={lang.code} 
                    onClick={() => changeLanguage(lang.code)} 
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px', 
                      border: 'none', 
                      background: selectedLanguage === lang.code ? '#f0f7ff' : 'transparent', 
                      cursor: 'pointer', 
                      textAlign: 'left', 
                      borderRadius: '8px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px', 
                      fontSize: '14px', 
                      color: selectedLanguage === lang.code ? '#6B4423' : '#333', 
                      fontWeight: selectedLanguage === lang.code ? '600' : '400', 
                      transition: 'all 0.2s' 
                    }} 
                    onMouseEnter={(e) => { 
                      if (selectedLanguage !== lang.code) { 
                        e.currentTarget.style.background = '#f8f8f8'; 
                      } 
                    }} 
                    onMouseLeave={(e) => { 
                      if (selectedLanguage !== lang.code) { 
                        e.currentTarget.style.background = 'transparent'; 
                      } 
                    }} 
                  > 
                    <span style={{ fontSize: '20px' }}>{lang.flag}</span> 
                    <div style={{ flex: 1 }}> 
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>{lang.nativeName}</div> 
                      <div style={{ fontSize: '11px', color: '#999' }}>{lang.name}</div> 
                    </div> 
                    {selectedLanguage === lang.code && ( 
                      <span style={{ color: '#6B4423' }}>✓</span> 
                    )} 
                  </button> 
                ))} 
          
                {/* International Languages Section */} 
                <div style={{ 
                  padding: '8px 12px', 
                  fontSize: '11px', 
                  fontWeight: '600', 
                  color: '#666', 
                  textTransform: 'uppercase', 
                  borderTop: '1px solid #f0f0f0', 
                  borderBottom: '1px solid #f0f0f0', 
                  marginTop: '8px' 
                }}> 
                  🌍 International Languages 
                </div> 
                {languages.filter(lang => lang.category === 'international').map(lang => ( 
                  <button 
                    key={lang.code} 
                    onClick={() => changeLanguage(lang.code)} 
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px', 
                      border: 'none', 
                      background: selectedLanguage === lang.code ? '#f0f7ff' : 'transparent', 
                      cursor: 'pointer', 
                      textAlign: 'left', 
                      borderRadius: '8px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px', 
                      fontSize: '14px', 
                      color: selectedLanguage === lang.code ? '#6B4423' : '#333', 
                      fontWeight: selectedLanguage === lang.code ? '600' : '400', 
                      transition: 'all 0.2s', 
                      direction: lang.rtl ? 'rtl' : 'ltr' // Support RTL for Arabic 
                    }} 
                    onMouseEnter={(e) => { 
                      if (selectedLanguage !== lang.code) { 
                        e.currentTarget.style.background = '#f8f8f8'; 
                      } 
                    }} 
                    onMouseLeave={(e) => { 
                      if (selectedLanguage !== lang.code) { 
                        e.currentTarget.style.background = 'transparent'; 
                      } 
                    }} 
                  > 
                    <span style={{ fontSize: '20px' }}>{lang.flag}</span> 
                    <div style={{ flex: 1 }}> 
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>{lang.nativeName}</div> 
                      <div style={{ fontSize: '11px', color: '#999' }}>{lang.name}</div> 
                    </div> 
                    {selectedLanguage === lang.code && ( 
                      <span style={{ color: '#6B4423' }}>✓</span> 
                    )} 
                  </button> 
                ))} 
              </div> 
            )} 
          </div>
          
          <button onClick={onClose} style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            padding: '4px'
          }}>
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="chatbot-messages" style={{
        flex: '1 1 auto',
        overflowY: 'auto',
        padding: '16px',
        backgroundColor: '#F5F5F5',
        WebkitOverflowScrolling: 'touch',
      }}>
        {messages.map(msg => (
          <div key={msg.id} style={{ marginBottom: '16px' }}>
            <div style={{
              display: 'flex',
              justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start'
            }}>
              <div style={{
                maxWidth: '75%',
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: msg.sender === 'user' ? '#6B4423' : 'white',
                color: msg.sender === 'user' ? 'white' : '#333',
                whiteSpace: 'pre-wrap',
                fontSize: '14px',
                lineHeight: '1.5',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {msg.text}
              </div>
            </div>
            
            {msg.quickReplies && msg.sender === 'bot' && (
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px', 
                marginTop: '12px',
                justifyContent: 'flex-start'
              }}>
                {msg.quickReplies.map((reply, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickReply(reply)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '20px',
                      border: '1px solid #6B4423',
                      background: 'white',
                      color: '#6B4423',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#6B4423';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.color = '#6B4423';
                    }}
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
            <div style={{
              padding: '12px 16px',
              borderRadius: '12px',
              backgroundColor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  backgroundColor: '#6B4423',
                  animation: 'bounce 1.4s infinite ease-in-out both',
                  animationDelay: '-0.32s'
                }}></div>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  backgroundColor: '#6B4423',
                  animation: 'bounce 1.4s infinite ease-in-out both',
                  animationDelay: '-0.16s'
                }}></div>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  backgroundColor: '#6B4423',
                  animation: 'bounce 1.4s infinite ease-in-out both'
                }}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="chatbot-input" style={{
        padding: '12px 14px',
        backgroundColor: 'white',
        borderTop: '1px solid #E5E5E5',
        flexShrink: 0,
        paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
      }}>
        <form onSubmit={(e) => {
          e.preventDefault();
          if (inputMessage && inputMessage.trim()) {
            sendMessage(inputMessage);
          }
        }} style={{ display: 'flex', gap: '8px' }}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder={
              selectedLanguage === 'hi' ? "अपना संदेश लिखें..." :
              selectedLanguage === 'ta' ? "உங்கள் செய்தியை எழுதுங்கள்..." :
              selectedLanguage === 'te' ? "మీ సందేశాన్ని టైప్ చేయండి..." :
              "Type your message..."
            }
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid #DDD',
              borderRadius: '24px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
          <button
            type="submit"
            disabled={isLoading || !inputMessage.trim()}
            style={{
              padding: '12px 16px',
              backgroundColor: '#6B4423',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              cursor: isLoading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
              opacity: isLoading || !inputMessage.trim() ? 0.5 : 1,
              transition: 'all 0.2s'
            }}
          >
            <Send size={18} />
          </button>
        </form>
      </div>

      {/* CSS Animation for loading dots */}
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { 
            transform: scale(0);
            opacity: 0.5;
          } 
          40% { 
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default Chatbot;